import React, { FC } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { Pressable, View } from 'react-native'

import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'

type Props = {
  /** Unique token symbols, one chip each. */
  chips: string[]
  /** The Select's search form control — watched here so toggling a chip
   * highlights it without re-rendering the (heavier) list header above it. */
  control: Control<{ search: string }>
  setSearch: (name: 'search', value: string) => void
}

// Horizontal-wrapping row of token-name chips shown at the top of the token
// picker list. Tapping a chip writes its symbol into the search field, which
// the existing list filter uses to surface that token on every chain. Tapping
// the active chip again clears the filter.
const TokenNameFilterChips: FC<Props> = ({ chips, control, setSearch }) => {
  const { theme } = useTheme()
  const search = useWatch({ control, name: 'search' })
  const normalizedSearch = (search || '').trim().toLowerCase()

  if (!chips.length) return null

  return (
    <View
      style={[
        { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
        spacings.mbSm
      ]}
    >
      {chips.map((symbol) => {
        const isActive = normalizedSearch === symbol.toLowerCase()

        return (
          <Pressable
            key={symbol}
            onPress={() => setSearch('search', isActive ? '' : symbol)}
            style={({ hovered }: any) => ({
              paddingVertical: 6,
              paddingHorizontal: 14,
              borderRadius: 100,
              backgroundColor: (isActive
                ? theme.primary
                : theme.tertiaryBackground) as string,
              opacity: hovered && !isActive ? 0.85 : 1
            })}
          >
            <Text
              fontSize={13}
              weight="medium"
              color={(isActive ? '#FFFFFF' : theme.secondaryText) as string}
            >
              {symbol}
            </Text>
          </Pressable>
        )
      })}
    </View>
  )
}

export default React.memo(TokenNameFilterChips)
